import React, { useEffect, useState } from "react";
import { FiTrash } from "react-icons/fi";
import TableCloud from "../TableEngine/TableCloud";
import { useLanguage } from "../../Translations/LenguageContext";
import { useNavigate, useParams } from "react-router-dom";
import FormatDateTime from "../../Functions/FormatDate";
import LoadingCloudStorage from "./LoadingCloudStorage";
import ErrorPage from "../../Components/ErrorPage";
import ActionFileModal from "./ActionFileModal";
import { FaCheck } from "react-icons/fa";

const DeletedCloud = () => {
    const { t } = useLanguage()
    const { project_id } = useParams()
    const navigate = useNavigate()
    const user_id = sessionStorage.getItem("user_id")
    const [loading, setLoading] = useState(false)
    const [files, setFiles] = useState([])
    const [filteredFiles, setFilteredFiles] = useState([])
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFileID, setSelectedFileID] = useState("")
    const [action, setAction] = useState(null)
    const [animateReset, setAnimateReset] = useState(false)
    const [searchQuery, setSearchQuery] = useState("");
    const [showShareModal, setShowShareModal] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState([]);


    useEffect(() => {
        if (user_id) {
            fetchDeletedFiles();
        } else {
            setError("User ID is missing. Please log in again.");
            setLoading(false);
        }

    }, [user_id]);


    const fetchDeletedFiles = async () => {
        setLoading(true)
        try {
            const response = await fetch("https://backend.erioon.com/get_deleted_files", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ user_id, project_id }),
            });
            setLoading(false)
            if (response.ok) {
                const result = await response.json();
                const files = result.files || {};
                const recordsArray = Object.values(files)
                if (Array.isArray(recordsArray)) {
                    setFiles(recordsArray);
                    setFilteredFiles(recordsArray);
                } else {
                    throw new Error("Invalid response format");
                }
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Failed to load files.");
            }
        } catch (err) {
            setLoading(false)
            setError("Failed to connect to the server. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setSearchQuery("")
        setFilteredFiles(files);
        setAnimateReset(true)
        setTimeout(() => {
            setAnimateReset(false)
        }, 1000);
    };

    const tableData = filteredFiles.map((file) => {
        const sizeInKB = file.size_kb || 0;
        const formattedSize =
            sizeInKB >= 1000
                ? `${(sizeInKB / 1024).toFixed(2)} MB`
                : `${sizeInKB.toFixed(2)} KB`;

        return {
            name: {
                label: t.cloud.cloud_table.filename,
                value: file.filename || "N/A",
                type: "name"
            },
            timestamp: {
                label: t.cloud.cloud_table.created,
                value: FormatDateTime(file.timestamp) || "N/A",
                type: "timestamp"
            },
            _id: {
                label: t.cloud.cloud_table.id,
                value: file._id || "N/A",
                type: "_id"
            },
            size: {
                label: t.cloud.cloud_table.size,
                value: formattedSize,
                type: "size"
            },
            folder_path: {
                label: t.cloud.cloud_table.folder_path,
                value: file.folder_path || "N/A",
                type: "folder_path"
            },
            status: {
                label: t.cloud.cloud_table.status,
                value: file.status || "N/A",
                type: "status"
            },
        };
    });

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);

        if (query.trim() === "") {
            setFilteredFiles(files);
            return;
        }

        const filtered = files.filter((file) => {
            return (
                file._id.toLowerCase().includes(query) ||
                file.filename.toLowerCase().includes(query) ||
                file.status.toLowerCase().includes(query) ||
                file.timestamp.toLowerCase().includes(query) ||
                file.file_url.toLowerCase().includes(query)
            );
        });

        setFilteredFiles(filtered);
    };

    const handleActionClick = (fileId, actionModal) => {
        setSelectedFiles((prevFiles) => [...new Set([...prevFiles, ...[].concat(fileId)])]);
        setShowDeleteModal(true);
        setAction(actionModal);
    };

    const handleCloseActionModal = () => {
        setShowDeleteModal(false);
        setAction(null);
    };

    const handleCheckboxChange = (fileId, isChecked) => {
        setSelectedFiles((prevSelected) =>
            isChecked ? [...prevSelected, fileId] : prevSelected.filter((id) => id !== fileId)
        );
    };

    const handleShareSelected = () => {
        if (selectedFiles.length > 0) {
            setShowShareModal(true);
        }
    };

    const handleOpenShareModal = (fileId, action) => {
        setShowShareModal(true);
        setSelectedFiles((prevIds) => [...prevIds, fileId])
    };



    if (loading) {
        return <LoadingCloudStorage />;
    }

    if (error) {
        return <ErrorPage />;
    }

    return (
        <div className="main_view_container">
            <p className="new_section_main_view_title">
                <i><FiTrash /></i> {t.cloud.deleted_files}
            </p>

            <div className="filter_container">
                {selectedFiles.length > 0 && (
                    <>
                        <button className="filter_btn" onClick={() => handleActionClick(selectedFiles, "activate")}>
                            <i><FaCheck /></i> {t.cloud.restore}
                        </button>
                        <button className="filter_btn" onClick={() => handleActionClick(selectedFiles, "delete_permanently")}>
                            <i><FiTrash /></i> {t.cloud.delete_permanently}
                        </button>
                    </>
                )}
                <button
                    className={`filter_btn`}
                    onClick={resetFilters}
                >
                    <i className={`fa-solid fa-arrows-rotate ${animateReset ? "activated_animation" : ""}`}></i>
                </button>
                <div className="filter_input_cont">
                    <i className="fa-solid fa-magnifying-glass"></i>
                    <input
                        className="filter_input"
                        placeholder={t.search}
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                </div>
            </div>

            <TableCloud data={tableData} handleActionClick={handleActionClick} handleOpenShareModal={handleOpenShareModal} selectedFiles={selectedFiles}
                handleCheckboxChange={handleCheckboxChange} />
            <ActionFileModal
                project_id={project_id}
                show={showDeleteModal}
                handleClose={handleCloseActionModal}
                selectedFiles={selectedFiles}
                fetchCloud={fetchDeletedFiles}
                actionModal={action}
                privPub={"public"}
                setSelectedFiles={setSelectedFiles}
            />
        </div>
    )
}

export default DeletedCloud